import { Injectable } from '@angular/core';
import { createServiceLogger } from '../utils/logger.util';
import { Observable, defer, from } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { AuthenticationService } from './auth.service';
import { SettingsService } from '@/pages/settings/settings.service';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Firestore, collection, doc, getDoc, getDocs, query, where } from '@angular/fire/firestore';
import { ConnectionService } from './connection.service';
import { sanitizeDataForFirestore, calculateSanitizationSizeReduction } from '../utils/firestore-sanitization.util';
import { LazyGlitchTipService } from './lazy-glitchtip.service';
import { MediaUploadService } from './media-upload.service';
import {
  MEDIA_SCHEMA_VERSION,
  toLegacyCategories,
  toLegacyDefects,
  toLegacyHeaders,
  toLegacyPlaces,
  PLACE_STATS_COLLECTION,
  PLACE_STATS_DOC_ID,
  type CatalogCategoryDoc,
  type CatalogDefectDoc,
  type CatalogPlaceDoc,
  type CatalogTemplateDoc,
  type LegacyCategory,
  type LegacyDefect,
  type LegacyHeader,
  type LegacyPlace,
  type MediaUploadContext,
  type PlaceSuggestionStats,
} from '@mtng-reports/models';
import { AppStorageService } from './app-storage.service';
import { energyMonitoringService } from './energy-monitoring.service';
import type { BaseReportData, StoreReportResponse } from '../../../../models/src/reports';
import type { ReportPublicationTelemetry } from './report-publication-telemetry.service';
import { reportanReportUrl, type ReportPostType } from './reportan-report-url';

const CATALOG_COMPANY_ID = 'default';
type CatalogSource = 'bedek' | 'retivut';

interface Media {
  id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  source_url: string;
  sourceUrl?: string;
  provider?: 'wordpress' | 'r2';
  visibility?: 'private' | 'published';
  wordpressMediaId?: number;
}

export interface CreateReportInput {
  crmFollowUp?: BaseReportData['crmFollowUp'];
  customer_address: any;
  content: any;
  customer_name: any;
  customer_phone?: string;
  header: any;
  customer_id: any;
  property_type: any;
  title?: string;
  ID?: string;
  json_content?: any;
}

export interface PublishedReport extends StoreReportResponse {
  /** Reportan Web editor URL for the stored report. */
  link: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  currentSite: string;
  private logger = createServiceLogger('ApiService');

  constructor(
    private authService: AuthenticationService,
    private settingsService: SettingsService,
    private functions: Functions,
    private firestore: Firestore,
    private connectionService: ConnectionService,
    private glitchTipService: LazyGlitchTipService,
    private mediaUploadService: MediaUploadService,
    private storage: AppStorageService,
  ) {}

  /**
   * Publication succeeds on the Firestore save alone; WordPress is no longer called
   * (adr/publish-angular-reports-to-firestore-only.md).
   */
  async createReport(
    post_type: ReportPostType,
    reportData: CreateReportInput,
    publicationTelemetry?: ReportPublicationTelemetry,
  ): Promise<PublishedReport> {
    const { customer_address, content, customer_name, customer_phone, header, customer_id, property_type } = reportData;
    await this.authService.waitForAuthReady();
    const user = this.authService.currentUser;
    if (!user?.email) {
      throw new Error('User not authenticated');
    }

    // Sanitize the original data to remove base64 images before storing in Firestore
    const sanitizedOriginalData = sanitizeDataForFirestore(content.originalData);

    // Calculate size reduction
    const sizeStats = calculateSanitizationSizeReduction(content.originalData, sanitizedOriginalData);

    // Log the size reduction
    console.log('📊 Document Size Optimization:', {
      originalSize: `${sizeStats.originalSizeKB.toFixed(2)} KB`,
      sanitizedSize: `${sizeStats.sanitizedSizeKB.toFixed(2)} KB`,
      reduction: `${sizeStats.reductionKB.toFixed(2)} KB`,
      reductionPercentage: `${sizeStats.reductionPercentage.toFixed(2)}%`,
      firestoreLimit: '1024 KB',
      withinLimit: sizeStats.withinFirestoreLimit,
      percentageOfLimit: `${sizeStats.percentageOfLimit.toFixed(2)}%`,
    });

    // Warn if still approaching limit even after sanitization
    if (!sizeStats.withinFirestoreLimit || sizeStats.percentageOfLimit > 80) {
      console.warn('⚠️ Document still large after sanitization:', {
        size: `${sizeStats.sanitizedSizeKB.toFixed(2)} KB`,
        percentageOfLimit: `${sizeStats.percentageOfLimit.toFixed(2)}%`,
        recommendation: 'Consider further optimization or document splitting',
      });

      // Report to GlitchTip when documents are still large after sanitization
      this.glitchTipService.captureMessage('Document approaching size limit after sanitization', 'warning', {
        tags: {
          postType: post_type,
          documentSize: sizeStats.sanitizedSize.toString(),
          reductionPercentage: Math.round(sizeStats.reductionPercentage).toString(),
        },
        extra: {
          originalSizeKB: sizeStats.originalSizeKB,
          sanitizedSizeKB: sizeStats.sanitizedSizeKB,
          percentageOfLimit: sizeStats.percentageOfLimit,
        },
      });
    }

    const firebaseReportData = {
      customer_address,
      customer_name,
      customer_phone,
      customer_id,
      property_type,
      post_type,
      header,
      content: sanitizedOriginalData, // Store sanitized data instead of original
      mail: user.email,
      userId: user.uid,
      createdAt: new Date(),
      updatedAt: new Date(),
      mediaSchemaVersion: MEDIA_SCHEMA_VERSION,
      // Publication ends here, so Reportan shows the report as ready (מוכן), like Mobile.
      markReady: true,
      ...(reportData.crmFollowUp ? { crmFollowUp: reportData.crmFollowUp } : {}),
    } satisfies BaseReportData;

    let storedReport: StoreReportResponse;
    const storeReport = httpsCallable<typeof firebaseReportData, StoreReportResponse>(
      this.functions,
      'storeReport',
    );
    try {
      const store = () => storeReport(firebaseReportData);
      storedReport = (publicationTelemetry
        ? await publicationTelemetry.measure('store_report', store)
        : await store()).data;
    } catch (error) {
      console.error('Error storing report in Firebase', error);

      // Report error to GlitchTip with enhanced context
      this.glitchTipService.captureException(error as Error, {
        tags: {
          operation: 'storeReport',
          postType: post_type,
          errorType: error.code || 'firebase_error',
          platform: 'web',
          component: 'api_service',
        },
        extra: {
          errorDetails: error.message,
          errorCode: error.code || 'unknown',
          userId: user.uid,
          timestamp: new Date().toISOString(),
          reportTitle: reportData.title || 'No title',
          reportId: reportData.ID || 'No ID',
          postType: post_type,
          hasJsonContent: !!reportData.json_content,
          contentLength: reportData.json_content ? JSON.stringify(reportData.json_content).length : 0,
          sanitizedContentLength: JSON.stringify(sanitizedOriginalData).length,
          userAgent: navigator.userAgent,
          connectionType: (navigator as any)?.connection?.effectiveType || 'unknown',
          errorCategory:
            error.code === 'functions/unauthenticated'
              ? 'authentication'
              : error.code === 'functions/permission-denied'
                ? 'permission'
                : error.code === 'functions/internal'
                  ? 'internal_server'
                  : error.code === 'functions/unavailable'
                    ? 'network'
                    : 'unknown',
        },
        fingerprint: ['firebase-store-report', error.code || 'unknown'],
      });

      throw error;
    }

    return { ...storedReport, link: reportanReportUrl(post_type, storedReport.reportId) };
  }

  getBlob(b64Data) {
    const sliceSize = 1024;
    const contentType = 'image/webp';

    if (!b64Data || typeof b64Data !== 'string') {
      throw new Error('Invalid base64 data: data is null, undefined, or not a string');
    }

    // Remove existing data URI prefix if present
    b64Data = b64Data.replace(/data:image\/(jpeg|jpg|png|webp);base64,/gi, '');

    // Validate base64 string format
    if (!b64Data.trim()) {
      throw new Error('Invalid base64 data: empty string after prefix removal');
    }

    // Check if the string contains only valid base64 characters
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    if (!base64Regex.test(b64Data)) {
      throw new Error('Invalid base64 data: contains invalid characters: ' + b64Data);
    }

    // Ensure proper padding
    const paddingLength = (4 - (b64Data.length % 4)) % 4;
    const paddedB64Data = b64Data + '='.repeat(paddingLength);

    let byteCharacters;
    try {
      // Decode base64 with proper error handling
      byteCharacters = atob(paddedB64Data);
    } catch (error) {
      throw new Error('Failed to decode base64 data');
    }

    // Add timestamp to make the content unique
    const timestamp = new Date().getTime().toString();
    const modifiedByteCharacters =
      byteCharacters + String.fromCharCode(...timestamp.split('').map((c) => c.charCodeAt(0)));

    const byteArrays = [];
    const length = modifiedByteCharacters.length;

    for (let offset = 0; offset < length; offset += sliceSize) {
      const slice = modifiedByteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  media(data, context: MediaUploadContext, filename = 'mobile_img'): Observable<Media> {
    energyMonitoringService.recordUploadStarted(typeof data === 'string' ? data.length : undefined);
    return from(this.mediaUploadService.upload(data, filename, context)).pipe(
      finalize(() => energyMonitoringService.recordUploadFinished()),
    );
  }

  mediaWithProgress(
    data: string,
    context: MediaUploadContext,
    filename = 'mobile_img',
    progressCb: (progress: number) => void,
  ): Promise<Media> {
    energyMonitoringService.recordUploadStarted(data.length);
    return this.mediaUploadService
      .upload(data, filename, context, progressCb)
      .finally(() => energyMonitoringService.recordUploadFinished());
  }

  loadPlaces(counterData: Record<string, number>): Observable<LegacyPlace[]> {
    return this.readCatalog('places', async () =>
      toLegacyPlaces(await this.readCatalogDocs<CatalogPlaceDoc>('places'), counterData).sort(
        (a, b) => b.counter - a.counter,
      ),
    );
  }

  loadCategories(source: CatalogSource): Observable<LegacyCategory[]> {
    return this.readCatalog(`${source}_categories`, async () =>
      toLegacyCategories(await this.readCatalogDocs<CatalogCategoryDoc>('categories'), source),
    );
  }

  extractHebrewPart(text: string): string {
    if (!text) return '';

    // Decode HTML entities first
    text = text.replace(/&#\d+;/g, (match) => {
      const numeric = match.replace(/[&#;]/g, '');
      return String.fromCharCode(parseInt(numeric));
    });

    // Replace various types of dashes and quotation marks with a space
    text = text
      .replace(/[""]/g, '"') // Normalize quotes
      .replace(/–|—/g, '-') // Normalize dashes
      .replace(/\s*[-–—]\s*/g, ' '); // Replace dashes with space

    // Split by common separators and clean up
    const parts = text
      .split(/\s+/)
      .map((part) => part.trim())
      .filter(Boolean);

    // Filter parts that contain Hebrew characters
    const hebrewRegex = /[\u0590-\u05FF]/;
    const hebrewParts = parts.filter((part) => hebrewRegex.test(part)).filter(Boolean);

    // Join Hebrew parts with a space
    return hebrewParts.join(' ');
  }

  loadDefects(source: CatalogSource): Observable<LegacyDefect[]> {
    return this.readCatalog(`${source}_defects`, async () => {
      const [docs, categories, places] = await Promise.all([
        this.readCatalogDocs<CatalogDefectDoc>('defects', source),
        this.readCatalogDocs<CatalogCategoryDoc>('categories'),
        this.readCatalogDocs<CatalogPlaceDoc>('places'),
      ]);
      return toLegacyDefects(docs, { source, categories, places });
    }).pipe(
      map((defects) => {
        if (!this.settingsService.hebrewOnly) {
          return defects;
        }
        return defects.map((defect) => ({
          ...defect,
          post_title: this.extractHebrewPart(defect.post_title),
        }));
      }),
    );
  }

  getHeaders(): Observable<LegacyHeader[]> {
    return this.readCatalog('headers', async () =>
      toLegacyHeaders(await this.readCatalogDocs<CatalogTemplateDoc>('templates')),
    );
  }

  getPlaceSuggestionStats(): Observable<PlaceSuggestionStats | null> {
    return this.readCatalog('placeSuggestionStats', async () => {
      const snap = await getDoc(
        doc(this.firestore, 'companies', CATALOG_COMPANY_ID, PLACE_STATS_COLLECTION, PLACE_STATS_DOC_ID),
      );
      return snap.exists() ? (snap.data() as PlaceSuggestionStats) : null;
    });
  }

  // Backoffice owns the catalog (adr/read-angular-and-backoffice-catalogs-from-firestore.md).
  // Serve the snapshot before the network so offline inspectors can select phrases.
  // Refresh only future reads: replacing an open form's catalog can disrupt manual selection.
  private readCatalog<T>(key: string, read: () => Promise<T>): Observable<T> {
    const storageKey = `catalog_${key}`;
    return defer(async () => {
      const snapshot = await this.storage.getItem<T>(storageKey).catch((error: unknown) => {
        this.logger.warn('Catalog snapshot not read', {
          operation: 'readCatalog',
          metadata: { key, error: String(error) },
        });
        return null;
      });
      const refresh = read().then((value) => {
        this.storage.setItem(storageKey, value).catch((error: unknown) =>
          this.logger.warn('Catalog snapshot not saved', {
            operation: 'readCatalog',
            metadata: { key, error: String(error) },
          }),
        );
        return value;
      });
      if (snapshot !== null) {
        // The service owns refresh after consumers such as firstValueFrom unsubscribe.
        // Catch failures here so an unavailable network cannot invalidate cached choices.
        refresh.catch((error: unknown) => this.logger.warn('Catalog refresh failed', {
          operation: 'readCatalog',
          metadata: { key, error: String(error) },
        }));
        return snapshot;
      }
      return refresh;
    });
  }

  private async readCatalogDocs<T>(name: string, type?: CatalogSource): Promise<T[]> {
    const ref = collection(this.firestore, 'companies', CATALOG_COMPANY_ID, name);
    const snapshot = await getDocs(type ? query(ref, where('type', '==', type)) : ref);
    // Offline collection queries can resolve with empty or incomplete memory-cache data.
    // Let readCatalog serve the durable snapshot instead of overwriting it with that data.
    if (snapshot.metadata.fromCache) {
      throw new Error(`Catalog ${name} is unavailable from the server`);
    }
    return snapshot.docs.map((document) => ({ id: document.id, ...document.data() }) as T);
  }

  private pingCache: { response: boolean; timestamp: number } | null = null;
  private pingPromise: Promise<boolean> | null = null;
  private readonly PING_CACHE_TTL = 3 * 60 * 1000; // 3 minutes in milliseconds

  /** Probe Functions via getMediaUploadSettings; cache successes for 3 minutes. */
  async checkConnection(): Promise<boolean> {
    const now = Date.now();

    // Return cached result if still valid
    if (this.pingCache && now - this.pingCache.timestamp < this.PING_CACHE_TTL) {
      return this.pingCache.response;
    }

    // If there's already a ping in progress, wait for it
    if (this.pingPromise) {
      return this.pingPromise;
    }

    // Create a new ping promise
    energyMonitoringService.recordNetworkProbe();
    this.pingPromise = this.performPing(now);

    try {
      const result = await this.pingPromise;
      return result;
    } finally {
      // Clear the promise when done (success or failure)
      this.pingPromise = null;
    }
  }

  /**
   * Perform the actual ping request
   */
  private async performPing(timestamp: number): Promise<boolean> {
    try {
      const startTime = performance.now();
      await this.authService.waitForAuthReady();
      if (!this.authService.currentUser) throw new Error('User not authenticated');
      const probe = httpsCallable<Record<string, never>, unknown>(this.functions, 'getMediaUploadSettings', { timeout: 30000 });
      const response = (await probe({})).data;

      const rtt = performance.now() - startTime;
      const isConnected = typeof response === 'object' && response !== null;

      // Cache the result
      this.pingCache = {
        response: isConnected,
        timestamp,
      };

      // Update connection status based on the ping RTT
      this.connectionService.updateConnectionFromPing(rtt);

      return isConnected;
    } catch (error) {
      // Do not cache a failure: the next upload or publish attempt re-checks right away.
      // Parallel callers still share one in-flight check through pingPromise.
      this.pingCache = null;

      // Update connection status to offline when ping fails
      // This handles cases where navigator.onLine is true but there's no actual internet connectivity
      this.connectionService.setOfflineStatus();

      // Also refresh status to check navigator.onLine as a fallback
      if (!navigator.onLine) {
        this.connectionService.refreshStatus();
      }

      // Only capture meaningful errors, not routine network issues
      if (
        error instanceof Error &&
        !error.message.includes('Failed to fetch') &&
        !error.message.includes('Network request failed') &&
        !error.message.includes('timeout') &&
        error.name !== 'TimeoutError'
      ) {
        this.glitchTipService.captureException(error as Error, {
          tags: {
            component: 'ApiService',
            method: 'checkConnection',
          },
          extra: {
            connection: {
              isOnline: navigator.onLine,
              userAgent: navigator.userAgent,
            },
          },
        });
      }
      return false;
    }
  }
}
